import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, LogIn, Mail, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Signin = () => {
  const { user, profile, loading: authLoading, canAccessSuperAdmin, canAccessBrandAdmin } = useAuth();
  const [authMethod, setAuthMethod] = useState<'password' | 'magic'>('password');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Redirect authenticated users
  useEffect(() => {
    if (!authLoading && user && profile) {
      console.log('🔐 Signin: User authenticated, checking role...', profile.role);
      if (canAccessSuperAdmin() || canAccessBrandAdmin()) {
        console.log('🔐 Signin: Redirecting admin to /admin');
        navigate('/admin', { replace: true });
      } else {
        console.log('🔐 Signin: Redirecting user to /dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, profile, authLoading, canAccessSuperAdmin, canAccessBrandAdmin, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePasswordSignin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        // Provide specific error messages
        if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else {
          setError(error.message);
        }
        toast.error('Sign in failed. Please check your credentials.');
        setLoading(false);
      } else {
        toast.success('Successfully signed in!');
        // Keep loading=true until redirect happens
      }
    } catch (error: any) {
      setError('An unexpected error occurred');
      toast.error('An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email);

      if (error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setMessage('Password reset email sent! Check your inbox.');
        toast.success('Password reset email sent!');
        setShowForgotPassword(false);
      }
    } catch (error: any) {
      setError('Failed to send password reset email');
      toast.error('Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: formData.email
      });

      if (error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setMessage('Check your email for the magic link!');
        toast.success('Magic link sent to your email!');
      }
    } catch (error: any) {
      setError('An unexpected error occurred');
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/10 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <LogIn className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight">Welcome Back</CardTitle>
          <CardDescription>
            Sign in to access your dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex mb-4 p-1 bg-muted rounded-lg">
            <button
              type="button"
              onClick={() => setAuthMethod('password')}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                authMethod === 'password'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Password
            </button>
            <button
              type="button"
              onClick={() => setAuthMethod('magic')}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                authMethod === 'magic'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Magic Link
            </button>
          </div>

          {authMethod === 'password' ? (
            <form onSubmit={handlePasswordSignin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !formData.email || !formData.password}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign In
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(!showForgotPassword)}
                  className="text-sm text-muted-foreground hover:text-primary"
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              </div>

              {showForgotPassword && (
                <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Enter your email above, then click the button below to receive a password reset link.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleForgotPassword}
                    disabled={loading || !formData.email}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending Reset Email...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send Password Reset Email
                      </>
                    )}
                  </Button>
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !formData.email}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Magic Link...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Magic Link
                  </>
                )}
              </Button>
            </form>
          )}

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert className="mt-4">
              <Mail className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}


          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary hover:underline">
                Sign up here
              </Link>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Button
              variant="outline"
              onClick={async () => {
                setLoading(true);
                const { error } = await supabase.auth.signInWithPassword({
                  email: 'demo@example.com',
                  password: 'demo123'
                });
                if (error) {
                  // Create demo account if it doesn't exist
                  const { error: signUpError } = await supabase.auth.signUp({
                    email: 'demo@example.com',
                    password: 'demo123',
                    options: {
                      data: {
                        first_name: 'Demo',
                        last_name: 'User'
                      }
                    }
                  });
                  if (!signUpError) {
                    // Try signing in again
                    await supabase.auth.signInWithPassword({
                      email: 'demo@example.com',
                      password: 'demo123'
                    });
                  }
                }
                setLoading(false);
              }}
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                'Quick Demo Login'
              )}
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Home
            </Link>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/?logout=true';
              }}
              className="text-xs text-muted-foreground hover:text-primary underline"
            >
              Clear session & restart
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>Access levels:</p>
            <p className="text-xs mt-1">Super Admin • Brand Admin • User</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signin;